// @flow
import React from 'react';
import injectSheet from 'react-jss';
import { useHistory } from 'react-router-dom';
import Button from '~/app/components/Button';
import { S, A, TONES, DARK } from '~/app/ui/tokens';

type Props = {
  classes: Object
};

const EditorTypeSelector = ({ classes: c }: Props) => {
  const history = useHistory();
  const genesisBenefits = [
    'Ideal when you want to use existing templates and widgets',
    'Supports live streaming events',
    'No coding required',
  ];
  const studioBenefits = [
    'Code-first editor (HTML, CSS, and JavaScript)',
    'AI can generate full pages from text prompts and reference images',
    'Coding required: Basic coding knowledge is helpful for using this effectively',
  ];

  return (
    <div className={c.container}>
      <div className={c.hero}>
        <div className={c.title}>Choose Your Editor</div>
        <div className={c.subtitle}>
          Pick the page-building experience that best fits your goal. This choice is locked after creation.
        </div>
      </div>
      <div className={c.options}>
        <div className={c.cardGenesis}>
          <div className={c.cardTop}>
            <div className={c.iconWrapGenesis}>
              <i className="material-icons">view_quilt</i>
            </div>
            <div>
              <div className={c.cardEyebrow}>Classic</div>
              <div className={c.cardTitle}>Genesis</div>
            </div>
          </div>
          <div className={c.cardDescription}>
            Build using Genesis templates and widgets.
          </div>
          <div className={c.benefits}>
            {genesisBenefits.map((benefit) => (
              <div key={benefit} className={c.benefitRow}>
                <i className="material-icons">check_circle</i>
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          <Button
            primary
            size="lg"
            className={c.cta}
            onClick={() => history.push('/admin/template-browser')}
          >
            Continue with Genesis
          </Button>
          <div className={c.previewWrap}>
            <img
              src="https://genesis-cdn.ff.com/studio/Screenshot-2026-03-05-at-11.02.05-AM-default.jpg"
              alt="Classic Genesis editor"
              className={c.previewImg}
            />
          </div>
        </div>
        <div className={c.cardStudio}>
          {/* <div className={c.badge}>Recommended for modern layouts</div> */}
          <div className={c.cardTop}>
            <div className={c.iconWrapStudio}>
              <i className="material-icons">auto_awesome</i>
            </div>
            <div>
              <div className={c.cardEyebrow}>Modern</div>
              <div className={c.cardTitle}>Genesis Studio</div>
            </div>
          </div>
          <div className={c.cardDescription}>
            Build with a code-first Studio workflow. Write code manually or use AI to generate and iterate quickly.
          </div>
          <div className={c.benefits}>
            {studioBenefits.map((benefit) => (
              <div key={benefit} className={c.benefitRow}>
                <i className="material-icons">check_circle</i>
                <span>{benefit}</span>
              </div>
            ))}
          </div>
          <Button
            color="hsl(205, 82%, 42%)"
            size="lg"
            className={c.cta}
            onClick={() => history.push('/admin/page/new/studio')}
          >
            Continue with Studio
          </Button>
          <div className={c.previewWrap}>
            <img
              src="https://genesis-cdn.ff.com/studio/Screenshot-2026-03-05-at-11.00.29-AM-default.jpg"
              alt="Genesis Studio editor"
              className={c.previewImg}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    // A short, focused choice: the title and both cards are centred on any screen.
    alignItems: 'center',
    width: '100%',
    padding: '28px 32px 64px',
    minHeight: '100%',
    background: 'linear-gradient(180deg, hsl(210, 35%, 98%) 0%, hsl(0, 0%, 100%) 100%)',
    [DARK]: { background: S.chrome }
  },
  hero: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px'
  },
  title: {
    fontSize: '26px',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'hsl(218, 25%, 14%)',
    lineHeight: 1.2,
    [DARK]: { color: S.fg }
  },
  subtitle: {
    fontSize: '14px',
    color: 'hsl(218, 12%, 38%)',
    marginTop: '8px',
    maxWidth: '780px',
    lineHeight: 1.5,
    [DARK]: { color: S.muted }
  },
  options: {
    display: 'flex',
    gap: '20px',
    alignItems: 'stretch',
    justifyContent: 'center',
    flexWrap: 'wrap'
  },
  cardGenesis: {
    width: 'min(520px, 100%)',
    border: '1px solid hsl(212, 24%, 84%)',
    borderRadius: '12px',
    padding: '18px',
    background: A.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 10px 25px hsla(212, 30%, 20%, 0.08)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease',
    [DARK]: { borderColor: S.line }
  },
  cardStudio: {
    composes: '$cardGenesis',
    border: '1px solid hsl(205, 64%, 78%)',
    boxShadow: '0 12px 28px hsla(205, 50%, 30%, 0.13)',
    [DARK]: { borderColor: S.blue }
  },
  badge: {
    alignSelf: 'flex-start',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'hsl(205, 70%, 40%)',
    background: 'hsl(205, 100%, 96%)',
    border: '1px solid hsl(205, 80%, 88%)',
    borderRadius: '999px',
    padding: '4px 10px',
    [DARK]: { color: TONES.blue.fg, background: TONES.blue.bg, borderColor: TONES.blue.bg }
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  iconWrapGenesis: {
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: 'hsl(216, 35%, 95%)',
    color: 'hsl(216, 32%, 38%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .material-icons': {
      fontSize: '24px'
    },
    [DARK]: { background: S.chromeHover, color: S.muted }
  },
  iconWrapStudio: {
    composes: '$iconWrapGenesis',
    background: 'hsl(205, 100%, 95%)',
    color: 'hsl(205, 82%, 42%)',
    [DARK]: { background: TONES.blue.bg, color: S.blue }
  },
  cardEyebrow: {
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: 'hsl(218, 10%, 45%)',
    fontWeight: 700,
    marginBottom: '2px',
    [DARK]: { color: S.muted }
  },
  cardTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: 'hsl(218, 25%, 14%)',
    [DARK]: { color: S.fg }
  },
  cardDescription: {
    fontSize: '14px',
    lineHeight: 1.5,
    color: 'hsl(218, 12%, 35%)',
    minHeight: '42px',
    [DARK]: { color: S.muted }
  },
  previewWrap: {
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid hsl(212, 24%, 90%)',
    marginTop: '4px',
    width: '100%',
    height: '240px',
    backgroundColor: 'hsl(212, 24%, 94%)',
    [DARK]: { borderColor: S.line, backgroundColor: S.chrome }
  },
  previewImg: {
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    verticalAlign: 'top'
  },
  benefits: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '2px',
    marginBottom: '8px'
  },
  benefitRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    color: 'hsl(218, 22%, 22%)',
    fontSize: '13px',
    lineHeight: 1.4,
    '& .material-icons': {
      fontSize: '16px',
      color: 'hsl(205, 78%, 42%)',
      marginTop: '1px'
    },
    [DARK]: { color: S.fg, '& .material-icons': { color: S.blue } }
  },
  cta: {
    alignSelf: 'center',
    marginTop: 'auto',
    borderRadius: '8px',
    fontSize: '13px',
    padding: '9px 18px'
  },
};

export default injectSheet(styles)(EditorTypeSelector);
